import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  faBox,
  faCopy,
  faFlagCheckered,
  faPauseCircle,
  faPhoneSlash,
  faTrash
} from '@fortawesome/pro-solid-svg-icons';
import { Row, Col, Container } from '../../../grid';
import Icon from '../../atoms/icon';
import Button from '../../molecules/button';
import { SnoozePatientAction } from '../../organisms/action/snooze';
import { ResolvePatientAction } from '../../organisms/action/resolve';
import { ArchivePatientAction } from '../../organisms/action/archive';
import { PausePatientAction } from '../../organisms/action/pause';
import Chats from '../../organisms/chats';
import StatusBadge from '../../organisms/status/status-badge';
import StatusIndicator from '../../organisms/status/status-indicator';
import Toast from '../../organisms/toast';
import { GetFullName } from '../../../libs/patient';
import { FormatPhone } from '../../../libs/format';
import { FormatDate, GetCurrentTZ } from '../../../libs/time';
import { TriageCategory } from '../../../contexts/triage/types';
import { GetSecondaryCategoriesForPatient } from '../../../contexts/triage/utils';

import HeaderBanner from './banner';
import EditDescription from './edit-description';
import { useTriagePatient } from '../../../contexts/triage/patient';
import { useMetaData } from '../../../contexts/metadata';
import moment from 'moment-timezone';
import {
  getSuspensionDates,
  getPausedApps,
  isFullPause
} from '../../../libs/tasks';
import { useApps } from '../../../contexts/apps';

const PatientHeader = (): JSX.Element => {
  const { getAppName } = useApps();
  const tz = GetCurrentTZ();
  const { isReadOnly, isEmbedded, account, terms } = useMetaData();
  const {
    patientData: { category, patient, apps }
  } = useTriagePatient();
  const secondaryCategories = GetSecondaryCategoriesForPatient(patient, apps);
  let startDate: string | null = null;
  let endDate: string | null = null;

  if (patient.tasks) {
    const suspensionDates = getSuspensionDates(patient.tasks);
    if (suspensionDates) {
      startDate = suspensionDates.startDate || null;
      endDate = suspensionDates.endDate || null;
    }
  }
  // Check if suspension is in the future
  const upcomingSuspension =
    startDate && moment.tz(startDate, tz).isAfter(moment.tz(tz));

  // Determine if this is a program-specific pause
  const pausedAppKeys = patient.tasks ? getPausedApps(patient.tasks) : [];
  const pausedAppNames = pausedAppKeys.map(
    (key) => (getAppName && getAppName(key)) || key
  );
  const isProgramSpecificPause =
    pausedAppKeys.length > 0 && !isFullPause(patient.tasks);
  const getCopyText = (): string => {
    let copy = GetFullName(patient);
    copy += ' CSID: ' + patient.id;
    copy += ' Phone: ' + FormatPhone(patient.phone);
    if (patient.mrn) {
      copy += ` ${terms.mrn.capitalized}: ` + patient.mrn || '';
    }
    if (patient.dob) {
      copy += ' DOB: ' + patient.dob || '';
    }
    return copy;
  };
  let bg: string;
  switch (category) {
    case TriageCategory.Danger:
      bg = 'bg-grad-ltr-danger-brand-white';
      break;
    case TriageCategory.Warning:
      bg = 'bg-grad-ltr-warning-brand-white';
      break;
    case TriageCategory.Success:
      bg = 'bg-grad-ltr-success-brand-white';
      break;
    default:
      bg = 'bg-grad-ltr-none-brand-white';
  }
  const isCanceled = secondaryCategories.includes(TriageCategory.Canceled);
  const isCompleted = secondaryCategories.includes(TriageCategory.Completed);
  const isInactive = isCanceled && isCompleted;
  return (
    <>
      {secondaryCategories.includes(TriageCategory.Archived) && (
        <HeaderBanner
          icon={faBox}
          title={`${patient.firstName} Archived on ${FormatDate(
            patient.archivedAt
          )}`}
          className="bg-light-orange"
        >
          {patient.firstName} will not receive any communications until they are
          unarchived.
          <ArchivePatientAction patient={patient} className="ml-4" bare />
        </HeaderBanner>
      )}
      {upcomingSuspension && (
        <HeaderBanner
          icon={faPauseCircle}
          title={
            isProgramSpecificPause
              ? `Upcoming Programs Paused: ${pausedAppNames.join(', ')}`
              : `Upcoming Suspension: ${moment
                  .tz(startDate, tz)
                  .format('YYYY-MM-DD')} - ${
                  endDate ? moment.tz(endDate, tz).format('YYYY-MM-DD') : 'N/A'
                }`
          }
          className="bg-light-orange"
        >
          <span>
            {isProgramSpecificPause ? (
              <>
                {patient.firstName}'s{' '}
                <strong>{pausedAppNames.join(', ')}</strong> program(s) are
                scheduled to be paused from the start of{' '}
                {moment.tz(startDate, tz).format('YYYY-MM-DD')} to the start of{' '}
                {endDate
                  ? moment.tz(endDate, tz).format('YYYY-MM-DD')
                  : 'an unknown date'}
                . Other active programs will continue sending communications.
              </>
            ) : (
              <>
                {patient.firstName}'s service is scheduled to be paused from the
                start of {moment.tz(startDate, tz).format('YYYY-MM-DD')} to the
                start of{' '}
                {endDate
                  ? moment.tz(endDate, tz).format('YYYY-MM-DD')
                  : 'an unknown date'}
                .
              </>
            )}
          </span>
          <PausePatientAction patient={patient} apps={apps} fill />
        </HeaderBanner>
      )}
      {secondaryCategories.includes(TriageCategory.Paused) && (
        <HeaderBanner
          icon={faPauseCircle}
          title={
            isProgramSpecificPause
              ? `Programs Paused: ${pausedAppNames.join(', ')}`
              : `Service Paused on ${moment
                  .tz(startDate ? startDate : patient.suspendedAt, tz)
                  .format('YYYY-MM-DD')}`
          }
          className="bg-light-orange"
        >
          <span>
            {isProgramSpecificPause ? (
              <>
                {patient.firstName}'s{' '}
                <strong>{pausedAppNames.join(', ')}</strong> program(s) are
                paused
                {endDate
                  ? ` until ${moment.tz(endDate, tz).format('YYYY-MM-DD')}`
                  : ''}
                . Other active programs continue sending communications.
              </>
            ) : (
              <>
                {patient.firstName} will not receive any communications until
                service
                {endDate
                  ? ` resumes at the start of ${moment
                      .tz(endDate, tz)
                      .format('YYYY-MM-DD')}`
                  : ' resumed'}
                .
              </>
            )}
          </span>
          <PausePatientAction patient={patient} apps={apps} fill />
        </HeaderBanner>
      )}
      {patient.isLandline && (
        <HeaderBanner
          icon={faPhoneSlash}
          title="SMS Delivery Issue"
          className="bg-light-orange"
        >
          {patient.firstName}'s phone number may be a landline or is otherwise
          unable to receive SMS messages.
          {patient.isSuspended && (patient.twilioFailureCount || 0) >= 2
            ? ' Service has been paused due to repeated delivery failures. Update the phone number or switch to phone-call delivery to resume.'
            : ' SMS delivery may fail. Consider switching to phone-call delivery.'}
        </HeaderBanner>
      )}
      {isInactive ? (
        <HeaderBanner
          icon={faTrash}
          title="Programs Inactive"
          className="bg-light-orange"
        >
          All of {patient.firstName}'s programs are completed or canceled. We
          suggest archiving {patient.firstName} or prescribing a new program.
          <ArchivePatientAction patient={patient} className="ml-4" bare />
        </HeaderBanner>
      ) : isCanceled ? (
        <HeaderBanner
          icon={faTrash}
          title="Programs Removed"
          className="bg-light-orange"
        >
          All of {patient.firstName}'s programs have been removed. We suggest
          archiving {patient.firstName} or prescribing a new program.
          <ArchivePatientAction patient={patient} className="ml-4" bare />
        </HeaderBanner>
      ) : isCompleted ? (
        <HeaderBanner icon={faFlagCheckered} title="Programs Finished">
          All of {patient.firstName}'s programs have reached their end dates. We
          suggest archiving {patient.firstName} or prescribing a new program.
          <ArchivePatientAction patient={patient} className="ml-4" bare />
        </HeaderBanner>
      ) : null}
      <div className={`pt-2 pb-4 ${bg}`}>
        <Container fluid>
          <Row align="center">
            <Col
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {secondaryCategories.map((category, i) => (
                <StatusBadge
                  styles={{
                    marginLeft: '-13px'
                  }}
                  key={i}
                  category={category}
                  isPatient
                />
              ))}
              <StatusIndicator category={category} isPatient title={false} />
              <h2 className="mt-0 mb-0 font-serif text-serif-lg inline-block">
                {`${patient.firstName}${
                  patient.middleInitial ? ` ${patient.middleInitial}.` : ''
                } ${patient.lastName}`}
              </h2>
              <span className="text-small">
                <span className="ml-6 uppercase font-bold">Phone</span>
                <span className="ml-2">{FormatPhone(patient.phone)}</span>
                {patient.dob ? (
                  <React.Fragment>
                    <span className="ml-6 uppercase font-bold">DOB</span>
                    <span className="ml-2">{patient.dob}</span>
                  </React.Fragment>
                ) : null}
              </span>
              <CopyToClipboard
                text={getCopyText()}
                onCopy={() =>
                  Toast({
                    message: 'Copied to clipboard!',
                    intent: 'success',
                    timeout: 2000
                  })
                }
              >
                <Button
                  className="ml-4 text-small"
                  intent="primary"
                  icon={<Icon className="mr-2" icon={faCopy} />}
                  bare
                  variant="minimal"
                >
                  Copy
                </Button>
              </CopyToClipboard>
            </Col>
            <Col md="content">
              {account?.isChatEnabled && <Chats patient={patient} />}
              {!isReadOnly && !isEmbedded && (
                <React.Fragment>
                  <SnoozePatientAction patient={patient} />
                  <ResolvePatientAction patient={patient} />
                </React.Fragment>
              )}
            </Col>
          </Row>
          <Row align="center">
            <Col>
              <EditDescription patient={patient} />
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default PatientHeader;
